import { MatExpansionPanel } from '@angular/material/expansion';
import { Component, ElementRef, Inject, ViewChild, EventEmitter, AfterViewInit, isDevMode } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InventorySource } from 'src/app/acumatica/EntityServices/OrderPicking.service';
import { Shipment, ShipmentDetail, ShipmentDetailAllocation } from 'src/app/acumatica/EntityServices/OrderPicking.service';
import { StockItem } from 'src/app/acumatica/EntityServices/StockItem.service';
import { AppConfig } from 'src/app/app-config.service';
import { WMSService } from 'src/app/services/wms.service';
import { InventoryAdjustmentEditorComponent } from '../../../common/inventory-adjustment-editor/inventory-adjustment-editor.component';
import { InventoryLocationEditorComponent } from '../../../common/inventory-location-editor/inventory-location-editor.component';

@Component({
  templateUrl: './pick-completion-dialog.component.html',
  styleUrls: ['./pick-completion-dialog.component.css']
})
export class PickCompletionDialogComponent implements AfterViewInit {

  isDevMode = isDevMode();
  settings = AppConfig.settings;
  shipment!: Shipment;
  allocation!: ShipmentDetailAllocation;
  warehouseLocations!: string[];
  get detail(): ShipmentDetail { return this.allocation.shipmentDetail; }
  get item(): StockItem { return this.allocation.shipmentDetail.item!; }
  get source(): InventorySource { return this.allocation.source!; }

  @ViewChild('pickAdjustmentQtyEditor') pickAdjustmentQtyEditor!: InventoryAdjustmentEditorComponent;
  // public get adjustmentAmount(): number | undefined { return this.pickAdjustmentQtyEditor.adjustmentAmount }

  private otherPotentialLines: ShipmentDetail[];
  /** return the first line the piece source could potentially be reused for */
  get reuseLineDetail(): ShipmentDetail | null { return this.otherPotentialLines.length ? this.otherPotentialLines[0] : null }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: {
      shipment: Shipment,
      allocation: ShipmentDetailAllocation,
      warehouseLocations: string[]
    },
    public dialogRef: MatDialogRef<PickCompletionDialogComponent>,
    private dialog: MatDialog,
    private wms: WMSService
  ) {
    this.shipment = data.shipment;
    this.allocation = data.allocation;
    this.warehouseLocations = data.warehouseLocations;

    // determine if the piece can be reused -- heavy operation needs only done once
    this.otherPotentialLines = this.shipment.Details.filter(detail =>
      // has the same inventory id
      detail.InventoryID == this.allocation.InventoryID
      // is not yet completed
      && !detail.isComplete
      // is not pre-allocated or is pre-allocated for this piece
      && (!detail.PreAllocation || (detail.PreAllocation && detail.SuggestedPickingSource == this.source))
    );

    this.canReusePiece = !!this.otherPotentialLines.length;
  }

  ngAfterViewInit(): void {
    let focusInput: any;
    if (this.source.rolloutRequired && this.source.AdjustmentQty === undefined)
      focusInput = this.pickAdjustmentQtyEditor;
    else if (!this.needPieceRestock)
      focusInput = this.PrinterSelectorInput?.nativeElement;
    else
      focusInput = this.restockLocationComponent;

    setTimeout(() => focusInput.focus(), 100);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // #region Print Tags & Labels
  @ViewChild('PrinterExpansionPanel') PrinterExpansionPanel?: MatExpansionPanel;
  // @ViewChild('PrinterSelector') PrinterSelector!: HTMLInputElement;
  @ViewChild('PrinterSelector') PrinterSelectorInput?: ElementRef;

  _printerID?: string;
  get printerID(): string | undefined { return this._printerID; }
  set printerID(value: string | undefined) {
    let printer = this.wms.validatePrinterID(value);
    this._printerID = printer?.id;
  }

  /** event fired when a Printer is scanned / selected to have labels printed */
  PrintTagsLabels = new EventEmitter<string>();

  // handler for scanning a Printer ID tag
  onTablePrinterScanned(printerID?: any) {
    // event fires when value is nulled
    if (!printerID) return;

    this.PrintTagsLabels.emit(printerID);
    this.printerID = undefined;

    // if no need to restock -- complete after tags are printed
    if (!this.needPieceRestock) this.dialogRef.close();

    // do need restock, after scanning printer, focus back to Restock field
    else setTimeout(() => {
      this.restockLocationComponent.reset();
      this.PrinterExpansionPanel?.close();
    }, 100);
  }

  // #endregion

  // #region Adjust Remaining Inventory
  setFocusToElement(focusToElement: HTMLInputElement | ElementRef | undefined) {
    if (focusToElement === undefined) return;

    setTimeout(() => {
      if (focusToElement instanceof HTMLInputElement) focusToElement.focus();
      else focusToElement.nativeElement.focus();
    }, 100);
  }

  onAdjustmentEnterKey() {
    if (this.needPieceRestock) {
      this.restockLocationComponent.disabled = false;
      setTimeout(() => {
        this.restockLocationComponent.focus()
      }, 100);
    }
    else this.setFocusToElement(this.PrinterSelectorInput);
  }

  // #endregion

  // #region Put Away
  @ViewChild('RestockLocationComponent') restockLocationComponent!: InventoryLocationEditorComponent;

  /** return a boolean indicating if there is any yardage left to be restocked */
  get needPieceRestock(): boolean {
    return !!(this.source.RollBalance + this.source.AdjustmentAmt);
  }

  /** return a boolean indicating if there any other lines on the Shipment the piece could be used for */
  readonly canReusePiece: boolean;

  // handler for scanning a Bin Location tag
  onRestockBinScanned() {
    this.dialogRef.close();
  }

  // #endregion

}